import type { Action } from '@reduxjs/toolkit';
import get from 'lodash/fp/get';
import isNil from 'lodash/fp/isNil';
import type { Coords } from '../../common/types';
import type { RootDispatch, RootState } from '../../configuration/setup/store';
import { trace } from '../../configuration/setup/trace';
import { facadeApiV3 } from '../../store/facade/facadeApiV3';

const reverseGeocode = async (lat: number, lng: number, dispatch: RootDispatch): Promise<string> => {
    const response = await dispatch(
        facadeApiV3.endpoints.locateByCoordinate.initiate({
            locateByCoordinateRequest: {
                coordinate: {
                    latitude: lat,
                    longitude: lng,
                },
            },
        })
    ).unwrap();

    if (response?.name) {
        return response.name;
    }
    throw new Error('No corresponding address for the given coordinates!');
};

export const resolveAddress = (action: Action) => async (dispatch: RootDispatch) => {
    const coordinates = get('payload.coordinates')(action);
    if (isNil(coordinates) || isNil(coordinates.lat) || isNil(coordinates.lng)) {
        return;
    }

    try {
        const address = await reverseGeocode(coordinates.lat, coordinates.lng, dispatch);
        dispatch({
            ...action,
            payload: {
                ...action.payload,
                address,
            },
        });
        return address;
    } catch (error) {
        trace.warn('Failed to resolve address for coordinates, dispatching without address:', error);
        dispatch(action);
    }
};

export const reverseGeolocation =
    ({ lat, lng }: Coords) =>
    async (dispatch: RootDispatch, _getState: () => RootState) => {
        const response = await dispatch(
            facadeApiV3.endpoints.locateByCoordinate.initiate({
                locateByCoordinateRequest: {
                    coordinate: {
                        latitude: lat,
                        longitude: lng,
                    },
                },
            })
        ).unwrap();

        if (response) {
            return response;
        }
        trace.warn('Geocoding returned no results for coordinates:', `${lat},${lng}`);
        throw new Error('No corresponding address for the given coordinates!');
    };
